import { useState, useEffect } from 'react';
import { LayoutDashboard, Users, Smartphone, TrendingUp, Mail } from 'lucide-react';
import { dashboardApi, appApi } from '@/api/client';
import type { DashboardStats, App } from '@/types';

export default function Home() {
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [apps, setApps] = useState<App[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [statsRes, appsRes] = await Promise.all([
          dashboardApi.getStats(),
          appApi.getAll(),
        ]);
        if (statsRes.success) setStats(statsRes.data);
        if (appsRes.success) setApps(appsRes.data.slice(0, 5));
      } catch {
        console.error('Failed to fetch dashboard data');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const statCards = [
    {
      title: '应用总数',
      value: stats?.total_apps || 0,
      icon: Smartphone,
      color: 'from-blue-500 to-blue-600',
      bgColor: 'bg-blue-500/10',
    },
    {
      title: '用户总数',
      value: stats?.total_users || 0,
      icon: Users,
      color: 'from-green-500 to-green-600',
      bgColor: 'bg-green-500/10',
    },
    {
      title: '今日新增',
      value: stats?.today_users || 0,
      icon: TrendingUp,
      color: 'from-purple-500 to-purple-600',
      bgColor: 'bg-purple-500/10',
    },
    {
      title: '邮件发送',
      value: '0',
      icon: Mail,
      color: 'from-orange-500 to-orange-600',
      bgColor: 'bg-orange-500/10',
    },
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">仪表盘</h1>
          <p className="text-slate-400 mt-1">欢迎回来，查看您的管理概览</p>
        </div>
        <div className="text-right">
          <p className="text-slate-400 text-sm">当前时间</p>
          <p className="text-white font-medium">{new Date().toLocaleString('zh-CN')}</p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {statCards.map((card, index) => {
          const Icon = card.icon;
          return (
            <div
              key={index}
              className={`${card.bgColor} rounded-xl p-6 border border-slate-700/50 hover:border-slate-600 transition-all`}
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-slate-400 text-sm">{card.title}</p>
                  <p className="text-3xl font-bold text-white mt-2">{card.value}</p>
                </div>
                <div className={`w-12 h-12 rounded-lg bg-gradient-to-br ${card.color} flex items-center justify-center`}>
                  <Icon className="w-6 h-6 text-white" />
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-slate-800 rounded-xl p-6 border border-slate-700/50">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-white">最近应用</h2>
          </div>
          {loading ? (
            <div className="space-y-3">
              {[1, 2, 3].map((i) => (
                <div key={i} className="flex items-center gap-4 p-3 bg-slate-700/50 rounded-lg animate-pulse">
                  <div className="w-10 h-10 bg-slate-600 rounded-lg" />
                  <div className="flex-1 space-y-2">
                    <div className="h-4 bg-slate-600 rounded w-3/4" />
                    <div className="h-3 bg-slate-600 rounded w-1/2" />
                  </div>
                </div>
              ))}
            </div>
          ) : apps.length > 0 ? (
            <div className="space-y-3">
              {apps.map((app) => (
                <div key={app.id} className="flex items-center gap-4 p-3 bg-slate-700/50 rounded-lg hover:bg-slate-700 transition-colors">
                  <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-600 rounded-lg flex items-center justify-center">
                    <Smartphone className="w-5 h-5 text-white" />
                  </div>
                  <div className="flex-1">
                    <p className="text-white font-medium">{app.name}</p>
                    <p className="text-slate-400 text-sm">{app.id}</p>
                  </div>
                  <span className="text-slate-500 text-xs">
                    {new Date(app.created_at).toLocaleDateString()}
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-slate-400">
              <LayoutDashboard className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>暂无应用数据</p>
            </div>
          )}
        </div>

        <div className="bg-slate-800 rounded-xl p-6 border border-slate-700/50">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-white">最近注册用户</h2>
          </div>
          {loading ? (
            <div className="space-y-3">
              {[1, 2, 3].map((i) => (
                <div key={i} className="flex items-center gap-4 p-3 bg-slate-700/50 rounded-lg animate-pulse">
                  <div className="w-10 h-10 bg-slate-600 rounded-full" />
                  <div className="flex-1 space-y-2">
                    <div className="h-4 bg-slate-600 rounded w-3/4" />
                    <div className="h-3 bg-slate-600 rounded w-1/2" />
                  </div>
                </div>
              ))}
            </div>
          ) : stats?.recent_users && stats.recent_users.length > 0 ? (
            <div className="space-y-3">
              {stats.recent_users.map((user) => (
                <div key={user.id} className="flex items-center gap-4 p-3 bg-slate-700/50 rounded-lg">
                  <div className="w-10 h-10 bg-gradient-to-br from-green-500 to-teal-600 rounded-full flex items-center justify-center">
                    <Users className="w-5 h-5 text-white" />
                  </div>
                  <div className="flex-1">
                    <p className="text-white font-medium">{user.email}</p>
                    <p className="text-slate-400 text-sm">{user.source_app_name || '未知应用'}</p>
                  </div>
                  <span className="text-slate-500 text-xs">
                    {new Date(user.created_at).toLocaleDateString()}
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-slate-400">
              <Users className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>暂无用户数据</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}